import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import './AISettings.css';

const API = '/api/ai';

const DEFAULT_ACTIVITIES = [
  { key: 'hotel', name: 'Гостиничные решения', keywords: 'гостиница, отель, номер, pms', surveyPrompt: 'Уточни: тип объекта, кол-во номеров, нужные подсистемы (Wi‑Fi, ТВ, СКУД, телефония), сроки.', categories: [] },
  { key: 'networks', name: 'Сети и коммуникации', keywords: 'скс, волс, кабель, сеть, оптика', surveyPrompt: 'Уточни: площадь/этажность, тип кабеля, скорость порта, срок монтажа, наличие существующей инфраструктуры.', categories: [] },
  { key: 'video', name: 'Видеонаблюдение', keywords: 'камера, видеонаблюдение, cctv', surveyPrompt: 'Уточни: кол-во камер, разрешение, архив (дни), уличные/внутренние зоны, интеграции.', categories: [] },
  { key: 'automation', name: 'Автоматизация', keywords: 'умный дом, автоматизация, scada, knx', surveyPrompt: 'Уточни: тип объекта, контуры автоматизации, протоколы, масштаб (точки/датчики).', categories: [] },
  { key: 'equipment', name: 'Подбор оборудования', keywords: 'оборудование, поставка, коммутатор', surveyPrompt: 'Уточни: перечень позиций или задачу, бренды, количество, срок поставки.', categories: [] }
];

const emptyCondition = () => ({ name: '', unit: '', priceFrom: 0, priceTo: 0, note: '' });
const emptyItem = () => ({ name: '', unit: 'шт', priceFrom: 0, priceTo: 0, currency: 'RUB', note: '', conditions: [] });
const emptyCategory = () => ({ name: '', kind: 'service', items: [emptyItem()] });
const emptyActivity = () => ({ key: `activity_${Date.now()}`, name: '', keywords: '', surveyPrompt: '', categories: [emptyCategory()] });

const initCatalog = {
  enabled: true,
  approximateDisclaimer: 'Расчёт является ориентировочным и не является публичной офертой. Точную смету подготовит менеджер после осмотра объекта.',
  activityTypes: DEFAULT_ACTIVITIES,
  categories: []
};

const AIPriceCatalog = ({ token, showMsg }) => {
  const [catalog, setCatalog] = useState(initCatalog);
  const [loading, setLoading] = useState(true);
  const h = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/settings`, { headers: h });
      const pc = res.data?.priceCatalog || {};
      let activityTypes = pc.activityTypes?.length ? pc.activityTypes : [];
      if (!activityTypes.length && pc.categories?.length) {
        activityTypes = [{ key: 'general', name: 'Общий прайс', keywords: '', surveyPrompt: '', categories: pc.categories }];
      }
      if (!activityTypes.length) activityTypes = DEFAULT_ACTIVITIES;
      setCatalog({
        enabled: pc.enabled !== false,
        approximateDisclaimer: pc.approximateDisclaimer || initCatalog.approximateDisclaimer,
        activityTypes,
        categories: pc.categories || []
      });
    } catch {
      showMsg('Ошибка загрузки прайса');
    } finally {
      setLoading(false);
    }
  }, [h, showMsg]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    try {
      await axios.put(`${API}/admin/settings`, { priceCatalog: catalog }, { headers: h });
      showMsg('✅ Прайс сохранён');
    } catch {
      showMsg('Ошибка сохранения прайса');
    }
  };

  const patchActivity = (idx, patch) => {
    setCatalog(prev => {
      const activityTypes = [...prev.activityTypes];
      activityTypes[idx] = { ...activityTypes[idx], ...patch };
      return { ...prev, activityTypes };
    });
  };

  const patchCategory = (aIdx, cIdx, patch) => {
    setCatalog(prev => {
      const activityTypes = [...prev.activityTypes];
      const categories = [...activityTypes[aIdx].categories];
      categories[cIdx] = { ...categories[cIdx], ...patch };
      activityTypes[aIdx] = { ...activityTypes[aIdx], categories };
      return { ...prev, activityTypes };
    });
  };

  const patchItem = (aIdx, cIdx, iIdx, patch) => {
    setCatalog(prev => {
      const activityTypes = [...prev.activityTypes];
      const categories = [...activityTypes[aIdx].categories];
      const items = [...categories[cIdx].items];
      items[iIdx] = { ...items[iIdx], ...patch };
      categories[cIdx] = { ...categories[cIdx], items };
      activityTypes[aIdx] = { ...activityTypes[aIdx], categories };
      return { ...prev, activityTypes };
    });
  };

  if (loading) return <div className="ai-settings"><p>Загрузка прайса…</p></div>;

  return (
    <div className="ai-settings">
      <div className="ai-settings-section">
        <h2>💰 Прайс для ориентировочного расчёта AI</h2>
        <p className="price-hint">
          Прайс передаётся агенту только когда клиент спрашивает стоимость. AI сначала задаёт вопросы из опроса,
          затем даёт примерный расчёт с предупреждением о том, что сумма ориентировочная.
        </p>
        <label className="toggle-label" style={{ marginBottom: 16 }}>
          <input
            type="checkbox"
            checked={!!catalog.enabled}
            onChange={e => setCatalog(prev => ({ ...prev, enabled: e.target.checked }))}
          />
          <span className="toggle-switch"></span> Включить расчёт по прайсу
        </label>
        <div className="ai-form-group full-width">
          <label>Предупреждение клиенту (ориентировочный расчёт)</label>
          <textarea
            rows={2}
            value={catalog.approximateDisclaimer}
            onChange={e => setCatalog(prev => ({ ...prev, approximateDisclaimer: e.target.value }))}
          />
        </div>
      </div>

      {catalog.activityTypes.map((activity, aIdx) => (
        <div key={activity.key || aIdx} className="price-category-card" style={{ marginBottom: 24 }}>
          <div className="price-category-head">
            <input
              type="text"
              placeholder="Название вида деятельности"
              value={activity.name}
              onChange={e => patchActivity(aIdx, { name: e.target.value })}
            />
            <input
              type="text"
              placeholder="Ключ (hotel, networks…)"
              value={activity.key}
              onChange={e => patchActivity(aIdx, { key: e.target.value })}
              style={{ maxWidth: 160 }}
            />
            <button type="button" className="btn btn-sm danger" onClick={() => {
              setCatalog(prev => ({
                ...prev,
                activityTypes: prev.activityTypes.filter((_, i) => i !== aIdx)
              }));
            }}>Удалить вид</button>
          </div>
          <div className="ai-form-group full-width">
            <label>Ключевые слова (через запятую) — для определения вида деятельности в чате</label>
            <input value={activity.keywords || ''} onChange={e => patchActivity(aIdx, { keywords: e.target.value })} />
          </div>
          <div className="ai-form-group full-width">
            <label>Опрос перед расчётом (промпт для AI)</label>
            <textarea
              rows={3}
              value={activity.surveyPrompt || ''}
              onChange={e => patchActivity(aIdx, { surveyPrompt: e.target.value })}
              placeholder="Какие вопросы задать клиенту перед расчётом…"
            />
          </div>

          {(activity.categories || []).map((cat, cIdx) => (
            <div key={cIdx} style={{ marginLeft: 12, marginTop: 12, padding: 12, background: '#f8fafc', borderRadius: 8 }}>
              <div className="price-category-head">
                <input placeholder="Категория прайса" value={cat.name} onChange={e => patchCategory(aIdx, cIdx, { name: e.target.value })} />
                <select value={cat.kind || 'service'} onChange={e => patchCategory(aIdx, cIdx, { kind: e.target.value })}>
                  <option value="service">Услуги</option>
                  <option value="product">Товары</option>
                  <option value="activity">Работы</option>
                </select>
                <button type="button" className="btn btn-sm danger" onClick={() => {
                  patchActivity(aIdx, { categories: activity.categories.filter((_, i) => i !== cIdx) });
                }}>×</button>
              </div>

              {(cat.items || []).map((item, iIdx) => (
                <div key={iIdx}>
                  <div className="price-item-row">
                    <input placeholder="Позиция" value={item.name} onChange={e => patchItem(aIdx, cIdx, iIdx, { name: e.target.value })} />
                    <input type="number" placeholder="От" value={item.priceFrom ?? 0} onChange={e => patchItem(aIdx, cIdx, iIdx, { priceFrom: parseInt(e.target.value, 10) || 0 })} />
                    <input type="number" placeholder="До" value={item.priceTo ?? 0} onChange={e => patchItem(aIdx, cIdx, iIdx, { priceTo: parseInt(e.target.value, 10) || 0 })} />
                    <input placeholder="Ед." value={item.unit || ''} onChange={e => patchItem(aIdx, cIdx, iIdx, { unit: e.target.value })} />
                    <input placeholder="Примечание" value={item.note || ''} onChange={e => patchItem(aIdx, cIdx, iIdx, { note: e.target.value })} />
                    <button type="button" className="btn btn-sm danger" onClick={() => {
                      patchCategory(aIdx, cIdx, { items: cat.items.filter((_, i) => i !== iIdx) });
                    }}>×</button>
                  </div>
                  {(item.conditions || []).map((cond, condIdx) => (
                    <div key={condIdx} className="price-item-row" style={{ marginLeft: 20, opacity: 0.9 }}>
                      <span>+</span>
                      <input placeholder="Условие" value={cond.name} onChange={e => {
                        const conditions = [...(item.conditions || [])];
                        conditions[condIdx] = { ...conditions[condIdx], name: e.target.value };
                        patchItem(aIdx, cIdx, iIdx, { conditions });
                      }} />
                      <input type="number" placeholder="От" value={cond.priceFrom ?? 0} onChange={e => {
                        const conditions = [...(item.conditions || [])];
                        conditions[condIdx] = { ...conditions[condIdx], priceFrom: parseInt(e.target.value, 10) || 0 };
                        patchItem(aIdx, cIdx, iIdx, { conditions });
                      }} />
                      <input type="number" placeholder="До" value={cond.priceTo ?? 0} onChange={e => {
                        const conditions = [...(item.conditions || [])];
                        conditions[condIdx] = { ...conditions[condIdx], priceTo: parseInt(e.target.value, 10) || 0 };
                        patchItem(aIdx, cIdx, iIdx, { conditions });
                      }} />
                      <button type="button" className="btn btn-sm danger" onClick={() => {
                        patchItem(aIdx, cIdx, iIdx, { conditions: item.conditions.filter((_, i) => i !== condIdx) });
                      }}>×</button>
                    </div>
                  ))}
                  <button type="button" className="btn btn-sm btn-outline" onClick={() => {
                    patchItem(aIdx, cIdx, iIdx, { conditions: [...(item.conditions || []), emptyCondition()] });
                  }}>+ Условие</button>
                </div>
              ))}
              <button type="button" className="btn btn-sm btn-outline" onClick={() => {
                patchCategory(aIdx, cIdx, { items: [...(cat.items || []), emptyItem()] });
              }}>+ Позиция</button>
            </div>
          ))}
          <button type="button" className="btn btn-sm btn-outline" onClick={() => {
            patchActivity(aIdx, { categories: [...(activity.categories || []), emptyCategory()] });
          }}>+ Категория</button>
        </div>
      ))}

      <button type="button" className="btn btn-outline" onClick={() => {
        setCatalog(prev => ({ ...prev, activityTypes: [...prev.activityTypes, emptyActivity()] }));
      }}>+ Вид деятельности</button>

      <div style={{ marginTop: 24 }}>
        <button type="button" className="btn btn-primary btn-large" onClick={save}>💾 Сохранить прайс</button>
      </div>
    </div>
  );
};

export default AIPriceCatalog;
